/**
 * Model-backed analysis of pre-computed fleet facts.
 *
 * WHAT THIS IS FOR
 * ----------------
 * The intelligence composites compute real numbers by fanning out OCS calls —
 * usage, utilisation, cost per GB, days to exhaustion. That part is accurate and
 * must stay deterministic. What they lacked was judgement: they ended in a
 * hardcoded verdict sentence, which is how "high_cost_subscribers" answered a
 * question about total data cost with "Fleet margins look healthy."
 *
 * This adds the judgement and nothing else. The model receives facts it did not
 * compute and cannot change, and returns prose about them.
 *
 * THE NUMBERS RULE, AND HOW IT IS ENFORCED
 * ----------------------------------------
 * A model figure presented as a measurement is the worst failure this layer
 * could have — worse than no analysis, because it looks like data. Three
 * mechanisms, in order of strength:
 *
 *   1. STRUCTURE. The model answers through a tool schema whose fields are all
 *      strings and enums. There is no numeric field for it to fill, so it
 *      cannot return a figure as data.
 *   2. SUBSTITUTION. To cite a number it emits a placeholder — {balance} — and
 *      we replace it with the Worker-formatted `display` string. The rendered
 *      figure therefore always comes from the computed Fact.
 *   3. VALIDATION. Any bare digit left in the model's prose is rejected and the
 *      call falls back to the deterministic verdict. Cheap, and it catches a
 *      model that ignores (2).
 *
 * NEVER THROWS
 * ------------
 * Every failure — disabled, no credentials, wrong tier, out of budget, rate
 * limited, timeout, malformed output, validation failure — returns the
 * deterministic verdict with a `reason`. Callers have one code path, and a
 * Bedrock outage degrades the report rather than breaking the tool.
 *
 * NEVER SILENT, EITHER
 * --------------------
 * Degrading gracefully was right. Degrading INVISIBLY is what let Carrier ship
 * an AI product that never once ran in production: the deployed IAM user could
 * not invoke the configured inference profile, so every call fell back, returned
 * HTTP 200 and told nobody for two months.
 *
 * So a fallback is a reportable event, not only a return value. `onFallback`
 * hands the caller the reason, the underlying error, and whether the cause is
 * PERMANENT — an IAM denial fails identically on every future call, a throttle
 * does not. Treating those alike is how a real outage hides inside normal noise.
 *
 * The observer stays transport-free and is wrapped in try/catch, so this module
 * still needs no Sentry, no AWS and no Worker bindings, and a broken observer
 * cannot break the never-throws contract above.
 */

/** A pre-computed number or label. Computed in the Worker, never by the model. */
export interface Fact {
  /** Stable slug the model cites as {key}. */
  key: string;
  /** Human label, e.g. "Daily average". */
  label: string;
  value: number | string | boolean | null;
  unit: "bytes" | "days" | "pct" | "count" | "eur" | "kbps" | "iso8601" | "text";
  /** Worker-formatted for display, e.g. "2.14 GB". This is what gets rendered. */
  display: string;
}

/** The verdict today's threshold code produces. Always the fallback. */
export interface DeterministicVerdict {
  severity: string;
  findings: string[];
  actions: string[];
}

export interface AnalysisRequest {
  kind: string;
  /** The only tenant-derived content that reaches the model. */
  facts: Fact[];
  /** Pseudonymised labels only: { subscriber: "SUB-7f3a1c9e" }. Never a raw ICCID. */
  entities?: Record<string, string>;
  deterministic: DeterministicVerdict;
  maxOutputTokens?: number;
}

export type AnalysisPath = "model" | "deterministic";

export type FallbackReason =
  | "disabled"
  | "no_credentials"
  /** IAM refused the call. Permanent: it fails identically next time. */
  | "access_denied"
  /** The configured model id cannot be invoked as configured. Permanent. */
  | "model_unavailable"
  | "tier"
  | "budget_exhausted"
  | "rate_limited"
  | "timeout"
  | "error"
  | "no_facts"
  | "malformed"
  | "invented_number";

/**
 * Reasons that describe a broken deployment rather than a normal condition.
 *
 * This distinction is the point. `tier` and `budget_exhausted` are the product
 * working as sold; `rate_limited` is weather. These three mean nobody is getting
 * the AI they paid for and no future call will fix itself, so an operator should
 * be paged on them and on nothing else.
 *
 * `no_credentials` is in the list because it is only reachable once the feature
 * flag is on: a deployment that has enabled the model path and has no AWS keys
 * is misconfigured, not merely idle.
 */
export const PERMANENT_FALLBACK_REASONS: readonly FallbackReason[] = [
  "no_credentials",
  "access_denied",
  "model_unavailable",
];

export function isPermanentFallback(reason: FallbackReason | undefined): boolean {
  return reason !== undefined && PERMANENT_FALLBACK_REASONS.includes(reason);
}

/**
 * Reasons worth a warning rather than a page.
 *
 * These are per-call defects — Bedrock failed, timed out, or answered something
 * unusable. One is a blip; a sustained rate of them is an incident. Everything
 * not in this list and not in PERMANENT_FALLBACK_REASONS is the product
 * behaving as sold and reaches Sentry never, because paging on `tier` or
 * `budget_exhausted` is how the alert that matters gets muted.
 *
 * It lives beside PERMANENT_FALLBACK_REASONS so the MCP composites and the REST
 * API grade a fallback the same way. Two surfaces with two severity tables is
 * how one of them ends up paging on a throttle.
 */
export const SENTRY_WARN_FALLBACK_REASONS: readonly FallbackReason[] = [
  "error",
  "timeout",
  "malformed",
  "invented_number",
];

/** True when `reason` deserves a Sentry event at all, at any level. */
export function shouldReportFallback(reason: FallbackReason, permanent: boolean): boolean {
  return permanent || SENTRY_WARN_FALLBACK_REASONS.includes(reason);
}

/** What `onFallback` is told. Enough to report without re-deriving anything. */
export interface FallbackEvent {
  /** The composite that asked for analysis, e.g. "high_cost_subscribers". */
  kind: string;
  reason: FallbackReason;
  /** True when the cause is a misconfiguration, per PERMANENT_FALLBACK_REASONS. */
  permanent: boolean;
  /** The thrown value, when the fallback came from a failed call. */
  error?: unknown;
}

/**
 * Map a thrown value to the reason that describes it.
 *
 * Rate limiting is checked first because @carrier/ai marks it explicitly
 * (`isRateLimit`), and mistaking a throttle for a misconfiguration is exactly
 * the false alarm that gets an alert muted.
 *
 * The two permanent buckets are separated deliberately. `access_denied` means
 * this credential may not call Bedrock and is fixed in IAM. `model_unavailable`
 * means this model id cannot be invoked the way it is configured — the shape of
 * the June 2026 outage, where an inference-profile-only model was set as
 * BEDROCK_MODEL_ID — and is fixed in config. Sending an operator to the wrong
 * console makes an outage longer, not shorter.
 */
export function classifyModelError(err: unknown): FallbackReason {
  if (typeof err !== "object" || err === null) return "error";
  if ("isRateLimit" in err) return "rate_limited";

  const name = "name" in err ? String((err as { name: unknown }).name) : "";
  const message = err instanceof Error ? err.message : String(err);
  const status =
    "status" in err ? Number((err as { status: unknown }).status) : Number.NaN;

  if (
    name === "AbortError" ||
    name === "TimeoutError" ||
    /timed? ?out|aborted/i.test(message)
  ) {
    return "timeout";
  }
  if (
    status === 403 ||
    /AccessDenied|not authori[sz]ed|UnrecognizedClient|InvalidSignature|ExpiredToken|InvalidClientTokenId/i.test(
      message,
    )
  ) {
    return "access_denied";
  }
  if (
    status === 404 ||
    /ValidationException|ResourceNotFound|inference profile|on-demand throughput/i.test(
      message,
    )
  ) {
    return "model_unavailable";
  }
  return "error";
}

export interface AnalysisResult {
  path: AnalysisPath;
  reason?: FallbackReason;
  headline: string;
  assessment: string;
  recommendations: { action: string; why: string; priority: "now" | "soon" | "watch" }[];
  /** Fact keys the model actually cited, for auditing what it looked at. */
  usedFactKeys: string[];
  latencyMs?: number;
}

/**
 * The tool the model must answer through.
 *
 * Every field is a string or enum. There is deliberately no number anywhere —
 * that is mechanism (1). Adding a numeric field here would defeat the whole
 * design, so the shape is asserted by a test.
 */
export const ANALYSIS_TOOL_SCHEMA = {
  name: "report_analysis",
  description:
    "Report your judgement of the supplied fleet facts. Cite any figure as a " +
    "{placeholder} using the fact's key — never write the number yourself.",
  input_schema: {
    type: "object" as const,
    properties: {
      headline: {
        type: "string",
        description: "One sentence, the single most important thing. Cite figures as {key}.",
      },
      assessment: {
        type: "string",
        description:
          "2-4 sentences explaining what the facts mean and why. Cite figures as {key}. " +
          "Say plainly when the facts do not support a conclusion.",
      },
      recommendations: {
        type: "array",
        description: "Concrete actions. Empty array when none is warranted.",
        items: {
          type: "object",
          properties: {
            action: { type: "string", description: "What to do. Cite figures as {key}." },
            why: { type: "string", description: "Which fact drives it. Cite figures as {key}." },
            priority: { type: "string", enum: ["now", "soon", "watch"] },
          },
          required: ["action", "why", "priority"],
        },
      },
      used_fact_keys: {
        type: "array",
        description: "Keys of the facts you actually used.",
        items: { type: "string" },
      },
    },
    required: ["headline", "assessment", "recommendations", "used_fact_keys"],
  },
} as const;

export const ANALYSIS_SYSTEM_PROMPT = `You are a telecom fleet analyst. You are given facts that have ALREADY been measured. Your job is judgement, not arithmetic.

Rules:
1. NEVER write a number, percentage, or quantity yourself. To cite one, use its placeholder: {key}. The system substitutes the measured value.
2. Only cite facts that appear in the supplied list. Do not infer a figure that was not given to you.
3. If the facts do not support a conclusion, say so plainly. "There is not enough usage data to judge this" is a correct and useful answer.
4. Do not restate the facts as a list — the caller already has them. Explain what they MEAN.
5. Prefer no recommendation over a generic one. An empty recommendations array is fine.
6. Be concise and specific to this fleet. No filler.`;

/** Replace {key} with the Fact's display string — mechanism (2). */
export function substituteFacts(text: string, facts: Fact[]): string {
  const byKey = new Map(facts.map((f) => [f.key, f.display]));
  return text.replace(/\{([a-z0-9_]+)\}/gi, (whole, key: string) => {
    const display = byKey.get(key);
    return display ?? whole;
  });
}

/**
 * Reject prose that still carries a bare number after substitution — mechanism (3).
 *
 * Runs AFTER substitution, so legitimate figures are already `display` strings
 * from computed Facts. Anything numeric left over was written by the model.
 *
 * Deliberately tolerant of ordinals and small list markers ("top 3", "1."),
 * which carry no measurement claim. Everything else is treated as invented.
 *
 * Also catches a placeholder `substituteFacts` could not resolve. The model
 * is only allowed to cite keys from the supplied fact list; a `{key}` that
 * survives substitution means it cited a key that does not exist (a typo, a
 * hallucinated key name, or a stale prompt/fact-set mismatch). Such a key
 * carries no digits of its own — `{sampled_subscribers}` — so the bare-number
 * check above never sees it, and the raw template text was shipped straight
 * to the console verified live 2026-09-01. An unresolved placeholder is
 * exactly the "model wrote something the server did not verify" case this
 * guard exists to catch, so it is treated the same as an invented number.
 */
export function containsInventedNumber(rendered: string, facts: Fact[]): boolean {
  const displays = facts.map((f) => f.display).filter(Boolean);
  let stripped = rendered;
  for (const d of displays) {
    stripped = stripped.split(d).join(" ");
  }
  // Allow: a bare small integer used as an ordinal or list index.
  stripped = stripped.replace(/\b(?:top|first|next|last)\s+\d{1,2}\b/gi, " ");
  stripped = stripped.replace(/(^|\s)\d{1,2}[.)]\s/g, " ");
  if (/\d/.test(stripped)) return true;
  return /\{[a-z0-9_]+\}/i.test(rendered);
}

function fromDeterministic(
  req: AnalysisRequest,
  reason: FallbackReason,
): AnalysisResult {
  const d = req.deterministic;
  return {
    path: "deterministic",
    reason,
    headline: d.findings[0] ?? d.severity,
    assessment: d.findings.join(" "),
    recommendations: d.actions.map((action) => ({
      action,
      why: "From the measured thresholds.",
      priority: "soon" as const,
    })),
    usedFactKeys: [],
  };
}

/** Minimal shape of a Bedrock tool-use response, so this module stays transport-free. */
export interface ModelInvoker {
  (payload: {
    system: string;
    userMessage: string;
    tool: typeof ANALYSIS_TOOL_SCHEMA;
    maxTokens: number;
  }): Promise<{ toolInput: Record<string, unknown> } | null>;
}

export interface AnalyzeOptions {
  enabled?: boolean;
  reason?: FallbackReason;
  /**
   * Called once for every deterministic return, including the gate reasons.
   *
   * Every path, not only the failures: a surface that reports nothing when it
   * refuses on tier or budget cannot answer "how often does the model actually
   * run?", which is the question that went unasked for two months. The caller
   * decides which reasons deserve an alert and which only a counter — see
   * `isPermanentFallback`.
   *
   * Anything this throws is swallowed. Observability must never be able to turn
   * a degraded report into a broken tool.
   */
  onFallback?: (event: FallbackEvent) => void;
}

/**
 * Run the analysis, falling back to the deterministic verdict on ANY failure.
 *
 * `invoke` is injected so this module needs no AWS credentials, no fetch and no
 * Worker bindings — which is what lets both MCP surfaces and the tests share it.
 */
export async function analyzeWithModel(
  req: AnalysisRequest,
  invoke: ModelInvoker | null,
  opts: AnalyzeOptions = {},
): Promise<AnalysisResult> {
  /** Report, then return. One helper so no fallback path can forget to report. */
  const fall = (reason: FallbackReason, error?: unknown): AnalysisResult => {
    if (opts.onFallback) {
      try {
        opts.onFallback({
          kind: req.kind,
          reason,
          permanent: isPermanentFallback(reason),
          ...(error !== undefined ? { error } : {}),
        });
      } catch {
        // See AnalyzeOptions.onFallback: a broken observer is not an outage.
      }
    }
    return fromDeterministic(req, reason);
  };

  if (opts.reason) return fall(opts.reason);
  if (opts.enabled === false) return fall("disabled");
  if (!invoke) return fall("no_credentials");
  if (req.facts.length === 0) return fall("no_facts");

  const started = Date.now();
  let out: { toolInput: Record<string, unknown> } | null;
  try {
    out = await invoke({
      system: ANALYSIS_SYSTEM_PROMPT,
      userMessage: JSON.stringify({
        kind: req.kind,
        entities: req.entities ?? {},
        facts: req.facts.map((f) => ({
          key: f.key,
          label: f.label,
          value: f.value,
          unit: f.unit,
        })),
        deterministic_verdict: req.deterministic,
      }),
      tool: ANALYSIS_TOOL_SCHEMA,
      maxTokens: req.maxOutputTokens ?? 700,
    });
  } catch (err) {
    // The classifier replaces a two-way rate-limited/error split that collapsed
    // an IAM denial, a bad model id, a timeout and a genuine transient into one
    // indistinguishable "error". That collapse is why the June outage looked
    // like noise.
    return fall(classifyModelError(err), err);
  }

  if (!out?.toolInput) return fall("malformed");

  const t = out.toolInput as {
    headline?: unknown;
    assessment?: unknown;
    recommendations?: unknown;
    used_fact_keys?: unknown;
  };
  if (typeof t.headline !== "string" || typeof t.assessment !== "string") {
    return fall("malformed");
  }

  const headline = substituteFacts(t.headline, req.facts);
  const assessment = substituteFacts(t.assessment, req.facts);

  const rawRecs = Array.isArray(t.recommendations) ? t.recommendations : [];
  const recommendations = rawRecs
    .filter((r): r is Record<string, unknown> => r !== null && typeof r === "object")
    .map((r) => ({
      action: substituteFacts(String(r.action ?? ""), req.facts),
      why: substituteFacts(String(r.why ?? ""), req.facts),
      priority:
        r.priority === "now" || r.priority === "watch"
          ? (r.priority as "now" | "watch")
          : ("soon" as const),
    }))
    .filter((r) => r.action.length > 0);

  // Mechanism (3): anything numeric that is not a substituted display value was
  // written by the model. Reject the whole result rather than render part of it.
  const surfaces = [headline, assessment, ...recommendations.flatMap((r) => [r.action, r.why])];
  if (surfaces.some((s) => containsInventedNumber(s, req.facts))) {
    return fall("invented_number");
  }

  return {
    path: "model",
    headline,
    assessment,
    recommendations,
    usedFactKeys: Array.isArray(t.used_fact_keys)
      ? t.used_fact_keys.filter((k): k is string => typeof k === "string")
      : [],
    latencyMs: Date.now() - started,
  };
}
